import { type SxProps, type Theme } from "@mui/material/styles";

import { useDisclosure } from "hooks/use-disclosure";
import { Typography } from "ui/typography";
import { Button } from "ui/button";
import VersionEditModal, {
  FormType,
  SubmitFormProps,
} from "components/modals/version-edit-modal/version-edit-modal";
import { hasPermission } from "utilities/user";
import { Permission } from "types/auth";
import { useCurrentCampaign } from "hooks/api/use-campaigns";
import { useCurrentTouchpoint } from "hooks/api/use-current-touchpoint";
import { useCurrentUser } from "hooks/api/use-current-user";
import { TouchpointType } from "types/touchpoint";
import { getDisplayTouchpointType } from "utilities";
import { PROGRAM_TEMPLATES_CAMPAIGN_NAME } from "utilities/constants";

import Icons from "assets";

interface VersionNotFoundProps {
  touchpointType: TouchpointType;
  onCreateDraft: (props: SubmitFormProps) => void;
}

const START_DRAFT_BUTTON_SX: SxProps<Theme> = { mt: 3 };

export const NoVersionFound = ({ touchpointType, onCreateDraft }: VersionNotFoundProps) => {
  const createFirstDraftModal = useDisclosure();
  const currentCampaign = useCurrentCampaign();
  const currentTouchpoint = useCurrentTouchpoint();
  const currentUser = useCurrentUser();
  const isExternalClientPortal = hasPermission(currentUser, Permission.PERM_CLIENT_USER);

  const mayCreateDraft = !isExternalClientPortal;

  const emptyStateBody = isExternalClientPortal
    ? "Our team is still preparing this creative."
    : `You haven't built any versions of this ${getDisplayTouchpointType(touchpointType).toLowerCase()} yet.`;

  return (
    <div className="flex flex-col gap-2 py-14 items-center">
      <Typography variant="h3" sx={{ color: "secondary.main" }}>
        {isExternalClientPortal ? "Updates in progress" : "There's nothing here"}
      </Typography>
      <Typography variant="body1">{emptyStateBody}</Typography>
      {mayCreateDraft && (
        <Button
          startIcon={<Icons.Edit />}
          onClick={createFirstDraftModal.onOpen}
          sx={START_DRAFT_BUTTON_SX}>
          Start new draft
        </Button>
      )}

      <VersionEditModal
        {...createFirstDraftModal}
        isEmailType={touchpointType === TouchpointType.EMAIL}
        parentName={currentCampaign.name}
        name={currentTouchpoint.name}
        formType={FormType.NEW}
        onSubmit={onCreateDraft}
        placeholderNotes="Initial draft"
        touchpointId={currentTouchpoint.id}
        copyCode={currentTouchpoint?.copyCode}
        isTemplate={currentCampaign.name === PROGRAM_TEMPLATES_CAMPAIGN_NAME}
      />
    </div>
  );
};
